"use client"

import { useState, useEffect, useMemo } from "react"
import redirectURL from "../redirectURL"
import HistoricChartDrilldown from "../components/historicchartdrilldown.js";
import HistoricChartTwo from "../components/historiccharttwo.js"
import DashboardHeader from "../components/dashboardheader.js"
import DashboardSidebar from "../components/dashboardsidebar.js"
import DashboardWidgets from "../components/dashboardwidgets.js"
import SweetAlert from "react-bootstrap-sweetalert"
import CountUp from 'react-countup'; 

const HistoricSalesInsights = () => { 
  const [loadshow, setLoadshow] = useState("show-m")
  const [overlayBlock, setOverlayBlock] = useState("show-m")
  const [overlyBlock, setOverlyBlock] = useState("show-n")
  const [show, setShow] = useState(false)
  const [basicTitle, setBasicTitle] = useState("")
  const [basicType, setBasicType] = useState("default")
  const [skuOptions, setSkuOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [destinstionMapping, setDestinationMapping] = useState([])

  // Calculate dates
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const formattedYesterday = yesterday.toISOString().split("T")[0];

  const startDate30DaysBefore = new Date(yesterday);
  startDate30DaysBefore.setDate(yesterday.getDate() - 90);
  const formattedStartDate = startDate30DaysBefore.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(formattedStartDate);
  const [endDate, setEndDate] = useState(formattedYesterday);
  const [destinationType, setDestinationType] = useState("combination");

  // Global filters
  const [selectedDestinations, setSelectedDestinations] = useState([])
  const [selectedSkus, setSelectedSkus] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [selectedChannels, setSelectedChannels] = useState([])

  // Applied state
  const [appliedStartDate, setAppliedStartDate] = useState(startDate)
  const [appliedEndDate, setAppliedEndDate] = useState(endDate)
  const [appliedDestinations, setAppliedDestinations] = useState([])
  const [appliedSkus, setAppliedSkus] = useState([])
  const [appliedCategories, setAppliedCategories] = useState([])
  const [appliedChannels, setAppliedChannels] = useState([])
  const [hasAppliedOnce, setHasAppliedOnce] = useState(false)
  const [salesChartData, setSalesChartData] = useState({})

  // Options derived from current data
  const [destinationOptions, setDestinationOptions] = useState([]);

  const getSkuAndCategoryData = async (type) => {
    try {
      const params = {
        destination_type: type
      }
      const response = await redirectURL.post("inventory/getDistictSkus", params);
      
      if (response.status === 200 && response.data) {
        // Set SKU options
        if (response.data.data) {
          const combinedSkus = [...new Set([...response.data.data])];
          const formattedSkus = combinedSkus.map(sku => ({
            value: sku,
            label: sku
          }));
          setSkuOptions(formattedSkus);
        }

        // Set category options
        if (response.data.categories) {
          const uniqueCategories = [...new Set(response.data.categories)];
          const formattedCategories = uniqueCategories.map(cat => ({
            value: cat,
            label: cat
          }));
          setCategoryOptions(formattedCategories);
        }

        // Set channel options
        if (response.data.channels) {
          const uniqueChannels = [...new Set(response.data.channels)];
          const formattedChannels = uniqueChannels.map(channel => ({
            value: channel,
            label: channel
          }));
          setChannelOptions(formattedChannels);
        }

        // Set destination options from destData
        if (response.data.destData) {
          const uniqueDestinations = [...new Set(response.data.destData)];
          const formattedDestinations = uniqueDestinations.map(dest => ({
            value: dest,
            label: dest
          }));
          setDestinationOptions(formattedDestinations);
        }

        if (response.data.destMapping) {
          const destinationMap = {};
          for (const dest of response.data.destMapping) {
            destinationMap[dest.destination_store_name] = dest.destination_store_id;
          }
          setDestinationMapping(destinationMap);
        }
      }
    } catch (error) {
      console.error("Error fetching distinct SKUs, categories or destinations:", error);
      setBasicTitle("Failed to load options");
      setBasicType("error");
      setShow(true);
    }
  };

  const getCountersData = async (
    startDateProp,
    endDateProp,
    destinationsProp,
    skusProp,
    categoriesProp,
    channelsProp,
    destinationTypeProp
  ) => {
    setLoadshow("show-m");
    setOverlayBlock("show-m");
    
    try {
      const from_date = startDateProp ?? appliedStartDate;
      const to_date = endDateProp ?? appliedEndDate;
      const destinations = destinationsProp ?? appliedDestinations;
      const skus = skusProp ?? appliedSkus;
      const categories = categoriesProp ?? appliedCategories;
      const channels = channelsProp ?? appliedChannels;
      const destination_type = destinationTypeProp ?? destinationType;

      const dests = destinations.map(name => destinstionMapping[name])

      const params = {
        from_date,
        to_date,
        destinations: JSON.stringify(dests),
        skus: JSON.stringify(skus),
        sku_categories: JSON.stringify(categories),
        channel: JSON.stringify(channels),
        destination_type: destination_type === "combination" ? "combination" : destination_type
      };

      console.log("API Params:", params);

      const salesDataResponse = await redirectURL.post(
        "inventory/getSalesChartData",
        params
      );

      if (salesDataResponse.status === 200) {
        setSalesChartData(salesDataResponse.data?.data);
      }
      
      setLoadshow("show-n");
      setOverlayBlock("show-n");

    } catch (error) {
      console.error("Something Went Wrong:", error);
      setBasicTitle("Something Went Wrong!");
      setBasicType("error");
      setShow(true);
    }
  };

  // Fetch data on applied changes
  useEffect(() => {
    const getChartData = async () => {
      setLoadshow("show-m")
      setOverlayBlock("show-m")
      await getCountersData()
      await getSkuAndCategoryData(destinationType)
      setLoadshow("show-n")
      setOverlayBlock("show-n")
    }
    getChartData()
  }, [hasAppliedOnce])

  // Initial setup
  useEffect(() => {
    setAppliedStartDate(startDate)
    setAppliedEndDate(endDate)
    setAppliedDestinations(selectedDestinations)
    setAppliedSkus(selectedSkus)
    setAppliedCategories(selectedCategories)
    setAppliedChannels(selectedChannels)
  }, [])

  const closeAlert = () => {
    setShow(false)
    setLoadshow("show-n")
    setOverlayBlock("show-n")
    setOverlyBlock("show-n")
  }

  const counters = useMemo(() => {
    return {
      totalSalesDestinationWise: salesChartData?.total_sales_quantity || 0,
      totalSalesSkuWise: salesChartData?.average_sales_quantity_sku_wise || 0,
      totalDestinations: salesChartData?.active_destinations || 0,
      totalSkus: salesChartData?.total_skus || 0,
    }
  }, [salesChartData])

  const hideOverlySlideBlock = () => {
    setOverlyBlock("show-n");
    setLoadshow("show-n");
  };

  // Filter handlers
  const handleStartDateChange = (e) => setStartDate(e.target.value)
  const handleEndDateChange = (e) => setEndDate(e.target.value)
  const handleDestinationsChange = (arr) => setSelectedDestinations((arr || []).map((s) => s.value))
  const handleSkusChange = (arr) => setSelectedSkus((arr || []).map((s) => s.value))
  const handleCategoriesChange = (arr) => setSelectedCategories((arr || []).map((s) => s.value))
  const handleChannelsChange = (arr) => setSelectedChannels((arr || []).map((s) => s.value))

  const handleApplyFilters = async () => {
    // Update applied filters
    setAppliedStartDate(startDate)
    setAppliedEndDate(endDate)
    setAppliedDestinations(selectedDestinations)
    setAppliedSkus(selectedSkus)
    setAppliedCategories(selectedCategories)
    setAppliedChannels(selectedChannels)
    
    await getCountersData(
      startDate, 
      endDate, 
      selectedDestinations, 
      selectedSkus, 
      selectedCategories,
      selectedChannels,
      destinationType
    )
    setHasAppliedOnce(true)
  }

  const handleResetFilters = async () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const formattedYesterday = yesterday.toISOString().split("T")[0];

    const startDate30DaysBefore = new Date(yesterday);
    startDate30DaysBefore.setDate(yesterday.getDate() - 90);
    const formattedStartDate = startDate30DaysBefore.toISOString().split("T")[0];

    // Reset UI dates
    setStartDate(formattedStartDate);
    setEndDate(formattedYesterday);

    // Reset all selections
    setSelectedDestinations([]);
    setSelectedSkus([]);
    setSelectedCategories([]);
    setSelectedChannels([]);

    // Reset applied filters
    setAppliedStartDate(formattedStartDate)
    setAppliedEndDate(formattedYesterday);
    setAppliedDestinations([]);
    setAppliedSkus([]);
    setAppliedCategories([]);
    setAppliedChannels([]);
    
    await getCountersData(
      formattedStartDate,
      formattedYesterday,
      [],
      [],
      [],
      [],
      destinationType
    );
    setHasAppliedOnce(true);
  };

  const onToggleDestinationType = async (newType) => {
    try {
      // Reset all selections
      setSelectedDestinations([]);
      setSelectedSkus([]);
      setSelectedCategories([]);
      setSelectedChannels([]);
      
      setAppliedDestinations([]);
      setAppliedSkus([]);
      setAppliedCategories([]);
      setAppliedChannels([]);

      setLoadshow("show-m");
      setOverlayBlock("show-m");
      
      // Set the new type
      setDestinationType(newType);

      // Fetch new data with the new type
      await getCountersData(
        startDate,
        endDate,
        [],
        [],
        [],
        [],
        newType
      );

      // Fetch new options
      await getSkuAndCategoryData(newType);

    } catch (error) {
      console.error("Error switching destination type:", error);
      setBasicTitle("Failed to load data for selected destination type");
      setBasicType("error");
      setShow(true);
    } finally {
      setLoadshow("show-n");
      setOverlayBlock("show-n");
    }
  };

  const hasPerformanceData = counters?.totalSalesDestinationWise > 0 && counters?.totalSalesSkuWise > 0;
  
  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          padding: "20px",
        }}
      >
        <div style={{ zIndex: "9999999" }}>
          <SweetAlert
            show={show}
            type={basicType}
            title={basicTitle}
            onConfirm={closeAlert}
            style={{ fontSize: "9px" }}
          />
        </div>

        {/* Dashboard Header with Filters - Updated */}
        <DashboardHeader 
          counters={counters} 
          appliedStartDate={appliedStartDate} 
          appliedEndDate={appliedEndDate}
          // Filter props
          startDate={startDate}
          endDate={endDate}
          selectedDestinations={selectedDestinations}
          selectedSkus={selectedSkus}
          selectedCategories={selectedCategories}
          selectedChannels={selectedChannels}
          destinationOptions={destinationOptions}
          skuOptions={skuOptions}
          categoryOptions={categoryOptions}
          channelOptions={channelOptions}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onDestinationsChange={handleDestinationsChange}
          onSkusChange={handleSkusChange}
          onCategoriesChange={handleCategoriesChange}
          onChannelsChange={handleChannelsChange}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          destinationType={destinationType}
          onToggleDestinationType={onToggleDestinationType}
        />

        {/* Counters Section */}
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "24px",
            marginBottom: "24px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          }}
        >
          <h3 style={{ margin: "0 0 20px 0", fontSize: "20px", fontWeight: "700", color: "#333" }}>
            Performance Overview
          </h3>
          
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
            }}
          >
            <div
              style={{
                background: "#F6ECF1",
                borderRadius: "8px",
                padding: "16px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "4px", color: "black" }}>Active Destinations</div>
              <div style={{ fontSize: "24px", fontWeight: "700", color: "black" }}>
                <CountUp end={counters.totalDestinations} duration={2} separator="," />
              </div>
            </div>
            <div
              style={{
                background: "#F6ECF1",
                borderRadius: "8px",
                padding: "16px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "4px", color: "black" }}>Total SKUs</div>
              <div style={{ fontSize: "24px", fontWeight: "700", color: "black" }}>
                <CountUp end={counters.totalSkus} duration={2} separator="," />
              </div>
            </div>
            <div
              style={{
                background: "#F6ECF1",
                borderRadius: "8px",
                padding: "16px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "4px", color: "black" }}>Total Sales Quantity (Destination Wise)</div>
              <div style={{ fontSize: "24px", fontWeight: "700", color: "black" }}>
                <CountUp end={counters.totalSalesDestinationWise} duration={2} separator="," />
              </div>
            </div>
            <div
              style={{
                background: "#F6ECF1",
                borderRadius: "8px",
                padding: "16px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "4px", color: "black" }}>Avg. Sales Quantity (SKU Wise)</div>
              <div style={{ fontSize: "24px", fontWeight: "700", color: "black" }}>
                <CountUp end={counters.totalSkus > 0 ? Math.round(counters.totalSalesDestinationWise / counters.totalSkus) : 0} duration={2} separator="," />
              </div>
            </div>
          </div>
        </div>

        <DashboardWidgets
          top_performing_destinations={salesChartData?.top_performing_destinations || []}
          top_n_recent_sales={salesChartData?.top_n_recent_sales || []}
        />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "20px" }}>
            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              <HistoricChartDrilldown
                sales_trend_month_wise={salesChartData.sales_trend_month_wise || []}
                sales_trend_datewise={salesChartData.sales_trend_datewise || []}
              />
            </div>

            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              <HistoricChartTwo
                sales_summary_sku_wise={salesChartData?.sales_summary_sku_wise || []}
                destinations={appliedDestinations}
                skus={appliedSkus}
              />
            </div>
          </div>

          <DashboardSidebar
            salesChartData={salesChartData}
          />
        </div>

        {
          (!hasPerformanceData && selectedDestinations.length > 0) && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "320px",
                background: "#fff",
                borderRadius: "12px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                margin: "40px 0",
                border: "1.5px dashed #e0e0e0",
              }}
            >
              <div
                style={{
                  fontSize: "32px",
                  color: "#B9366D",
                  marginBottom: "12px",
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                &#9888;
              </div>
              <div style={{ fontSize: "20px", fontWeight: 700, color: "#B9366D", marginBottom: "8px" }}>
                No Data Found
              </div>
              <div style={{ fontSize: "15px", color: "#888", textAlign: "center", maxWidth: "340px" }}>
                We couldn't find any sales data for the selected filters or date range.<br />
                Try adjusting your filters or check back later.
              </div>
            </div>
          )
        }

        <div className={`loader ${loadshow}`}></div>
        <div className={`overlay-block ${overlayBlock}`}></div>
        <div
          className={"overly-block " + overlyBlock}
          onClick={hideOverlySlideBlock}
        ></div>
      </div>
    </>
  )
}

export default HistoricSalesInsights



// "use client"

// import { useState, useEffect, useMemo } from "react"
// import redirectURL from "../redirectURL"
// import HistoricChartDrilldown from "../components/historicchartdrilldown.js";//month and daywie

// import HistoricChartOne from "../components/historicchartone.js"//month wise

// import  HistoricChartDaywise  from "../components/historicchartdaywise.js" //day wise

// import HistoricChartTwo from "../components/historiccharttwo.js"
// import HistoricChartThree from "../components/historicchartthree.js"
// import HistoricChartFour from "../components/historicchartfour.js"
// import DashboardHeader from "../components/dashboardheader.js"
// import DashboardSidebar from "../components/dashboardsidebar.js"
// import DashboardWidgets from "../components/dashboardwidgets.js"
// import SweetAlert from "react-bootstrap-sweetalert"
// import CountUp from 'react-countup'; 


// const HistoricSalesInsights = () => { 
//   const [loadshow, setLoadshow] = useState("show-m")
//   const [overlayBlock, setOverlayBlock] = useState("show-m")
//   const [overlyBlock, setOverlyBlock] = useState("show-n")
//   const [show, setShow] = useState(false)
//   const [basicTitle, setBasicTitle] = useState("")
//   const [basicType, setBasicType] = useState("default")
//   const [skuOptions, setSkuOptions] = useState([]);

//   const [destinationType, setDestinationType] = useState("darkstore"); // "darkstore" or "warehouse"
//   const [destinstionMapping, setDestinationMapping] = useState([])

//   // Filter input state
//   // const [startDate, setStartDate] = useState("2023-08-01")
//   // const [endDate, setEndDate] = useState("2025-06-30")

//   // const [startDate, setStartDate] = useState("2023-08-01");
//   // // calculate yesterday dynamically
//   // const yesterday = new Date();
//   // yesterday.setDate(yesterday.getDate() - 1);

//   // const formattedYesterday = yesterday.toISOString().split("T")[0];

//   // const [endDate, setEndDate] = useState(formattedYesterday);


//   // Calculate yesterday
// const yesterday = new Date();
// yesterday.setDate(yesterday.getDate() - 1);
// const formattedYesterday = yesterday.toISOString().split("T")[0];

// // Calculate start date as 30 days before yesterday
// const startDate30DaysBefore = new Date(yesterday);
// startDate30DaysBefore.setDate(yesterday.getDate() - 30);
// const formattedStartDate = startDate30DaysBefore.toISOString().split("T")[0];

// // Use these in your state initialization
// const [startDate, setStartDate] = useState(formattedStartDate);
// const [endDate, setEndDate] = useState(formattedYesterday);

//   // Counters source data
//   const [countersData, setCountersData] = useState([])
//   const [countersDefaultData, setCountersDefaultData] = useState([])
//   const [initialCountersData, setInitialCountersData] = useState([])

//   // Global filters
//   const [selectedDestinations, setSelectedDestinations] = useState([])
//   const [selectedSkus, setSelectedSkus] = useState([])

//   // Applied state (APIs use these values)
//   const [appliedStartDate, setAppliedStartDate] = useState(startDate)
//   const [appliedEndDate, setAppliedEndDate] = useState(endDate)
//   const [appliedDestinations, setAppliedDestinations] = useState([])
//   const [appliedSkus, setAppliedSkus] = useState([])
//   const [hasAppliedOnce, setHasAppliedOnce] = useState(false)
//   const [salesChartData, setSalesChartData] = useState({})

//   // Options derived from current data
//   const [destinationOptions, setDestinationOptions] = useState([]);

//   const getSkuCodes = async (type) => {
//     try {
//       const params = {
//         destination_type: type ?? destinationType // Add this parameter
//       }
//       const response = await redirectURL.post("inventory/getDistictSkus", params);
      
//       if (response.status === 200 && response.data) {
//         // Set SKU options
//         if (response.data.data) {
//           const combinedSkus = [...new Set([...response.data.data])];
//           const formattedSkus = combinedSkus.map(sku => ({
//             value: sku,
//             label: sku
//           }));
//           setSkuOptions(formattedSkus);
//         }

//         // Set destination options from destData
//         if (response.data.destData) {
//           const uniqueDestinations = [...new Set(response.data.destData)];
//         //   console.log(uniqueDestinations, "uniqueDestinations");
          
//           const formattedDestinations = uniqueDestinations.map(dest => ({
//             value: dest,
//             label: dest
//           }));
//           setDestinationOptions(formattedDestinations);
//         }

//         if (response.data.destMapping) {
//            const destinationMap = {};

//             for (const dest of response.data.destMapping) {
//               destinationMap[dest.destination_store_name] = dest.destination_store_id;
//             }

//           setDestinationMapping(destinationMap);
//         }
//       }
//     } catch (error) {
//       console.error("Error fetching distinct SKUs or destinations:", error);
//       setBasicTitle("Failed to load SKU or destination options");
//       setBasicType("error");
//       setShow(true);
//     }
//   };
 
//   // const mappedDestinations = [];

//   // for (const destName of appliedDestinations) {
//   //   if (destinstionMapping[destName]) {
//   //     mappedDestinations.push(destinstionMapping[destName]);
//   //   }
//   // }

//     const getCountersData = async (
//       startDateProp,
//       endDateProp,
//       destinationsProp,
//       skusProp,
//       destinationTypeProp
//     ) => {

//       setLoadshow("show-m");
//       setOverlayBlock("show-m");
      
//       // const newType = destinationType === "darkstore" ? "darkstore" : "warehouse";
//       // setDestinationType(newType);

//       try {
//         const from_date = startDateProp ?? appliedStartDate;
//         const to_date = endDateProp ?? appliedEndDate;
//         const destinations = destinationsProp ?? appliedDestinations;
//         const skus = skusProp ?? appliedSkus;
//         const destination_type = destinationTypeProp ?? destinationType;

//        const dests = destinations.map(name => destinstionMapping[name])

//         const params = {
//           from_date,
//           to_date,
//           destinations: JSON.stringify(dests),
//           skus: JSON.stringify(skus),
//           destination_type
//         };

//         const salesDataResponse = await redirectURL.post(
//           "inventory/getSalesChartData",
//           params
//         );

//         if (salesDataResponse.status === 200) {
//           setSalesChartData(salesDataResponse.data?.data);
//         }
//          setLoadshow("show-n");
//       setOverlayBlock("show-n");

//       } catch (error) {
//         console.error("Something Went Wrong:", error);
//         setBasicTitle("Something Went Wrong!");
//         setBasicType("error");
//         setShow(true);
//       }
//     };


//   // Fetch counters on applied changes only
//   useEffect(() => {
//     const getChartData = async () => {
//       setLoadshow("show-m")
//       setOverlayBlock("show-m")
//       await getCountersData()
//       await getSkuCodes()
//       setLoadshow("show-n")
//       setOverlayBlock("show-n")
//     }
//     getChartData()
//   }, [hasAppliedOnce])

//   // Initial counters fetch on mount
//   useEffect(() => {
//     setAppliedStartDate(startDate)
//     setAppliedEndDate(endDate)
//     setAppliedDestinations(selectedDestinations)
//     setAppliedSkus(selectedSkus)
//   }, [])

//   const closeAlert = () => {
//     const fileInput = document.getElementById("bulkUploadFile")
//     if (fileInput !== null) {
//       fileInput.value = ""
//     }
//     setShow(false)
//     setLoadshow("show-n")
//     setOverlayBlock("show-n")
//     setOverlyBlock("show-n")
//   }

//   // // Counters
//   // const counters = useMemo(() => {
//   //   const all = Array.isArray(countersData) ? countersData : []
//   //   let total = 0
//   //   const dests = new Set()
//   //   const skus = new Set()

//   //   for (let i = 0; i < all.length; i++) {
//   //     const r = all[i]
//   //     total += Number(r.sale_quantity) || 0
//   //     if (r.destination_store_name) dests.add(r.destination_store_name)
//   //     if (r.sku_code) skus.add(r.sku_code)
//   //   }

//   //   // logic for totalSkus
//   //   let totalSkusCount = 0
//   //   if (appliedSkus && appliedSkus.length > 0) {
//   //     totalSkusCount = appliedSkus.length
//   //   } else {
//   //     totalSkusCount = skus.size > 0 ? skus.size : skuOptions.length
//   //   }

//   //   return {
//   //     totalSalesDestinationWise: total,
//   //     totalSalesSkuWise: total,
//   //     totalDestinations: dests.size,
//   //     totalSkus: totalSkusCount,
//   //   }
//   // }, [countersData, appliedSkus, skuOptions])

//   const counters = useMemo(() => {
//     // const all = Array.isArray(countersData) ? countersData : []
//     // let total = 0
//     // const dests = new Set()
//     // const skus = new Set()

//     // for (let i = 0; i < all.length; i++) {
//     //   const r = all[i]
//     //   total += Number(r.sale_quantity) || 0
//     //   if (r.destination_store_name) dests.add(r.destination_store_name)
//     //   if (r.sku_code) skus.add(r.sku_code)
//     // }

//     // // logic for totalSkus
//     // let totalSkusCount = 0
//     // if (appliedSkus && appliedSkus.length > 0) {
//     //   totalSkusCount = appliedSkus.length
//     // } else {
//     //   totalSkusCount = skus.size > 0 ? skus.size : skuOptions.length
//     // }

//     return {
//       totalSalesDestinationWise: salesChartData?.total_sales_quantity || 0,
//       totalSalesSkuWise: salesChartData?.average_sales_quantity_sku_wise || 0,
//       totalDestinations: salesChartData?.active_destinations || 0,
//       totalSkus: salesChartData?.total_skus || 0,
//     }
//   }, [salesChartData, appliedSkus, skuOptions])

//   const hideOverlySlideBlock = () => {
//     setOverlyBlock("show-n");
//     setLoadshow("show-n");
//   };

//   // Filter handlers
//   const handleStartDateChange = (e) => setStartDate(e.target.value)
//   const handleEndDateChange = (e) => setEndDate(e.target.value)
//   const handleDestinationsChange = (arr) => setSelectedDestinations((arr || []).map((s) => s.value))
//   const handleSkusChange = (arr) => setSelectedSkus((arr || []).map((s) => s.value))
  
//   const handleApplyFilters = async () => {
//     await getCountersData(
//       startDate, endDate, selectedDestinations, selectedSkus
//     )
//     // setAppliedStartDate(startDate)
//     // setAppliedEndDate(endDate)
//     // setAppliedDestinations(selectedDestinations)
//     // setAppliedSkus(selectedSkus)
//     // setHasAppliedOnce(true)
//   }

//   // const handleResetFilters = () => {
//   //   setStartDate("2023-08-01");
//   //   const yesterday = new Date();
//   //   yesterday.setDate(yesterday.getDate() - 1);
//   //   const formattedYesterday = yesterday.toISOString().split("T")[0];
//   //   setEndDate(formattedYesterday);

//   //   // Reset selected destinations and skus
//   //   setSelectedDestinations([]);
//   //   setSelectedSkus([]);

//   //   // Also reset applied filters
//   //   setAppliedStartDate("2023-08-01");
//   //   setAppliedEndDate(formattedYesterday);
//   //   setAppliedDestinations([]);
//   //   setAppliedSkus([]);
//   //   setHasAppliedOnce(true);
    
//   // }

//   // Destination switch functionality


//   const handleResetFilters = async() => {
//   // calculate yesterday first
//   const yesterday = new Date();
//   yesterday.setDate(yesterday.getDate() - 1);
//   const formattedYesterday = yesterday.toISOString().split("T")[0];

//   // calculate yesterday - 30 days
//   const startDate30DaysBefore = new Date(yesterday);
//   startDate30DaysBefore.setDate(yesterday.getDate() - 30);
//   const formattedStartDate = startDate30DaysBefore.toISOString().split("T")[0];

//   // Reset UI dates
//   setStartDate(formattedStartDate);
//   setEndDate(formattedYesterday);

//   // Reset selected destinations and skus
//   setSelectedDestinations([]);
//   setSelectedSkus([]);

//   // Reset applied filters
//   setAppliedStartDate(formattedStartDate)
//   setAppliedEndDate(formattedYesterday);
//   setAppliedDestinations([]);
//   setAppliedSkus([]);
//  await getCountersData(
//     formattedStartDate,
//     formattedYesterday,
//     [],
//     []
//   );
//   setHasAppliedOnce(true);
// };

//   const onToggleDestinationType = async () => {
//     try {
//       setSelectedDestinations([]);
//       setSelectedSkus([]);
//       setAppliedDestinations([]);
//       setAppliedSkus([]);

//       setLoadshow("show-m");
//       setOverlayBlock("show-m");
      
//       // Toggle the destination type
//       const newType = destinationType === "darkstore" ? "warehouse" : "darkstore";
//       setDestinationType(newType);

//       // Call API to get data for the new destination type
//       // const params = {
//       //   from_date: appliedStartDate,
//       //   to_date: appliedEndDate,
//       //   destinations: JSON.stringify(appliedDestinations),
//       //   skus: JSON.stringify(appliedSkus),
//       //   destination_type: newType // Add destination type parameter
//       // };
//       // const mappedDestinations = appliedDestinations.map(name => destinstionMapping[name])
//       // const params = {
//       //   from_date: startDate,
//       //   to_date: endDate,
//       //   destinations: JSON.stringify(mappedDestinations),
//       //   skus: JSON.stringify(appliedSkus),
//       //   destination_type: newType // Add destination type parameter
//       // };

//       await getCountersData(
//         startDate,
//         endDate,
//         appliedDestinations,
//         appliedSkus,
//         newType
//       )

//       await getSkuCodes(newType)

//       //  const salesDataResponse = await redirectURL.post("inventory/getSalesChartData", params);
//       //  if (salesDataResponse.status === 200) {
//       //     setSalesChartData(salesDataResponse.data?.data?.message)
//       // }
      
//       // const dailySalesResponse = await redirectURL.post("inventory/getDailySalesChartData", params);
      
//       // if (dailySalesResponse.status === 200 && dailySalesResponse.data && dailySalesResponse.data.data) {
//       //   const rows = JSON.parse(dailySalesResponse.data.data.grouped);
//       //   const rowsDefault = JSON.parse(dailySalesResponse.data.data.default);
//       //   setCountersData(rows);
//       //   setCountersDefaultData(rowsDefault)
        
//       //   // Update initial data if needed
//       //   if (!hasAppliedOnce && (!initialCountersData || initialCountersData.length === 0)) {
//       //     setInitialCountersData(rows);
//       //   }
//       // }
      
//       // Also refresh SKU options for the new destination type
//       // await getSkuCodes();
      
//     } catch (error) {
//       console.error("Error switching destination type:", error);
//       setBasicTitle("Failed to load data for selected destination type");
//       setBasicType("error");
//       setShow(true);
//     } finally {
//       setLoadshow("show-n");
//       setOverlayBlock("show-n");
//     }
//   };

//   const hasPerformanceData = counters?.totalSalesDestinationWise > 0 && counters?.totalSalesSkuWise > 0;
  
//   return (
//     <>
//       <div
//         style={{
//           minHeight: "100vh",
//           padding: "20px",
//         }}
//       >
//         <div style={{ zIndex: "9999999" }}>
//           <SweetAlert
//             show={show}
//             type={basicType}
//             title={basicTitle}
//             onConfirm={closeAlert}
//             style={{ fontSize: "9px" }}
//           />
//         </div>

//         {/* Dashboard Header with Filters */}
//           <DashboardHeader 
//             counters={counters} 
//             appliedStartDate={appliedStartDate} 
//             appliedEndDate={appliedEndDate}
//             // Filter props
//             startDate={startDate}
//             endDate={endDate}
//             selectedDestinations={selectedDestinations}
//             selectedSkus={selectedSkus}
//             destinationOptions={destinationOptions}
//             skuOptions={skuOptions}
//             onStartDateChange={handleStartDateChange}
//             onEndDateChange={handleEndDateChange}
//             onDestinationsChange={handleDestinationsChange}
//             onSkusChange={handleSkusChange}
//             onApplyFilters={handleApplyFilters}
//             onResetFilters={handleResetFilters}
//             destinationType={destinationType}
//             onToggleDestinationType={onToggleDestinationType}
//           />

//         {/* Counters Section - Replaced filters with counters */}
//         <div
//           style={{
//             background: "#fff",
//             borderRadius: "12px",
//             padding: "24px",
//             marginBottom: "24px",
//             boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
//           }}
//         >
//           <h3 style={{ margin: "0 0 20px 0", fontSize: "20px", fontWeight: "700", color: "#333" }}>
//             Performance Overview
//           </h3>
          
//           <div
//             style={{
//               display: "grid",
//               gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
//               gap: "16px",
//             }}
//           >
//             <div
//               style={{
//                 background: "#F6ECF1",
//                 borderRadius: "8px",
//                 padding: "16px",
//                 textAlign: "center",
//               }}
//             >
//               <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "4px", color: "black" }}>Active Destinations</div>
//               <div style={{ fontSize: "24px", fontWeight: "700", color: "black" }}>
//                 {/* {counters.totalDestinations} */}
//                 <CountUp end={counters.totalDestinations} duration={2} separator="," />
//               </div>
//             </div>
//             <div
//               style={{
//                 background: "#F6ECF1",
//                 borderRadius: "8px",
//                 padding: "16px",
//                 textAlign: "center",
//               }}
//             >
//               <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "4px", color: "black" }}>Total SKUs</div>
//               <div style={{ fontSize: "24px", fontWeight: "700", color: "black" }}>
//                 {/* {counters.totalSkus} */}
//                 <CountUp end={counters.totalSkus} duration={2} separator="," />
//                 </div>
//             </div>
//             <div
//               style={{
//                 background: "#F6ECF1",
//                 borderRadius: "8px",
//                 padding: "16px",
//                 textAlign: "center",
//               }}
//             >
//               <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "4px", color: "black" }}>Total Sales Quantity (Destination Wise)</div>
//               <div style={{ fontSize: "24px", fontWeight: "700", color: "black" }}>
//                 {/* {counters.totalSalesDestinationWise.toLocaleString()} */}
//                 <CountUp end={counters.totalSalesDestinationWise} duration={2} separator="," />
//               </div>
//             </div>
//             <div
//               style={{
//                 background: "#F6ECF1",
//                 borderRadius: "8px",
//                 padding: "16px",
//                 textAlign: "center",
//               }}
//             >
//               <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "4px", color: "black" }}>Avg. Sales Quantity (SKU Wise)</div>
//               <div style={{ fontSize: "24px", fontWeight: "700", color: "black" }}>
//                 {/* {counters.totalSkus > 0
//                   ? Math.round(counters.totalSalesDestinationWise / counters.totalSkus).toLocaleString()
//                   : "0"} */}
//                   <CountUp end={counters.totalSkus > 0 ? Math.round(counters.totalSalesDestinationWise / counters.totalSkus) : 0} duration={2} separator="," />
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Dashboard Widgets */}
//           <DashboardWidgets
//           top_performing_destinations={salesChartData?.top_performing_destinations || []}
//           top_n_recent_sales={salesChartData?.top_n_recent_sales || []}
//         />


//         {/* Main Content Area */}
//           <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "24px" }}>
//             <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "20px" }}>

//           {/* month and day */}
//           <div
//             style={{
//               background: "#fff",
//               borderRadius: "12px",
//               boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
//               overflow: "hidden",
//             }}
//           >
//             <HistoricChartDrilldown
//               sales_trend_month_wise={salesChartData.sales_trend_month_wise || []}
//               sales_trend_datewise={salesChartData.sales_trend_datewise || []}
//             />
//           </div>

//             {/* month */}
//               {/* <div
//                 style={{
//                   background: "#fff",
//                   borderRadius: "12px",
//                   boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
//                   overflow: "hidden",
//                 }}
//               >
//                 <HistoricChartOne
//                   sales_trend_month_wise={salesChartData?.sales_trend_month_wise || []}
//                   // startDate={appliedStartDate}
//                   // endDate={appliedEndDate}
//                   // destinations={appliedDestinations}
//                   // skus={appliedSkus}
//                   // destinationType={destinationType} 
//                 />
//               </div> */}

//              {/* daywise */}
//                {/* <div
//                 style={{
//                   background: "#fff",
//                   borderRadius: "12px",
//                   boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
//                   overflow: "hidden",
//                 }}
//               >
//                 < HistoricChartDaywise 
//                   sales_trend_datewise={salesChartData?.sales_trend_datewise || []}
//                   // startDate={appliedStartDate}
//                   // endDate={appliedEndDate}
//                   // destinations={appliedDestinations}
//                   // skus={appliedSkus}
//                   // destinationType={destinationType} 
//                 />
//               </div> */}













//               <div
//                 style={{
//                   background: "#fff",
//                   borderRadius: "12px",
//                   boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
//                   overflow: "hidden",
//                 }}
//               >
//                 <HistoricChartTwo
//                   sales_summary_sku_wise={salesChartData?.sales_summary_sku_wise || []}
//                   destinations={appliedDestinations}
//                   skus={appliedSkus}
//                   // startDate={appliedStartDate}
//                   // endDate={appliedEndDate}
//                   // destinationType={destinationType} // Add this
//                 />
//               </div>

//               {/* <div
//                 style={{
//                   background: "#fff",
//                   borderRadius: "12px",
//                   boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
//                   overflow: "hidden",
//                 }}
//               >
//                 <HistoricChartThree
//                   sales_summary_destination_store_wise={salesChartData?.sales_summary_destination_store_wise}
//                   // startDate={appliedStartDate}
//                   // endDate={appliedEndDate}
//                   // destinations={appliedDestinations}
//                   // skus={appliedSkus}
//                   // destinationType={destinationType} // Add this
//                 />
//               </div> */}

//               {/* <div
//                 style={{
//                   background: "#fff",
//                   borderRadius: "12px",
//                   boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
//                   overflow: "hidden",
//                 }}
//               >
//                 <HistoricChartFour
//                   sales_summary_sku_wise={salesChartData?.sales_summary_sku_wise || []}
//                   // startDate={appliedStartDate}
//                   // endDate={appliedEndDate}
//                   // destinations={appliedDestinations}
//                   // skus={appliedSkus}
//                   // destinationType={destinationType} // Add this
//                 />
//               </div> */}
//             </div>

//             <DashboardSidebar
//               salesChartData={salesChartData}
//               // countersData={countersData}
//               // appliedStartDate={appliedStartDate}
//               // appliedEndDate={appliedEndDate}
//             />
//           </div>

//         {
//           (!hasPerformanceData && selectedDestinations.length > 0) && (
//             <div
//               style={{
//                 display: "flex",
//                 flexDirection: "column",
//                 alignItems: "center",
//                 justifyContent: "center",
//                 minHeight: "320px",
//                 background: "#fff",
//                 borderRadius: "12px",
//                 boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
//                 margin: "40px 0",
//                 border: "1.5px dashed #e0e0e0",
//               }}
//             >
//               <div
//                 style={{
//                   fontSize: "32px",
//                   color: "#B9366D",
//                   marginBottom: "12px",
//                   fontWeight: 700,
//                   lineHeight: 1,
//                 }}
//               >
//                 &#9888;
//               </div>
//               <div style={{ fontSize: "20px", fontWeight: 700, color: "#B9366D", marginBottom: "8px" }}>
//                 No Data Found
//               </div>
//               <div style={{ fontSize: "15px", color: "#888", textAlign: "center", maxWidth: "340px" }}>
//                 We couldn't find any sales data for the selected filters or date range.<br />
//                 Try adjusting your filters or check back later.
//               </div>
//             </div>
//           )
//         }

//         <div
//           className={`loader ${loadshow}`}></div>
//         <div className={`overlay-block ${overlayBlock}`}></div>
//         <div
//           className={"overly-block " + overlyBlock}
//           onClick={hideOverlySlideBlock}
//         ></div>
//       </div>
//     </>
//   )
// }

// export default HistoricSalesInsights